"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { Transition } from "@headlessui/react";
import AlertDialog from "./AlertDialog";
import LaborRatesManagement from "./LaborRatesManagement";
import SubAgreementTemplatesManagement from "./SubAgreementTemplatesManagement";
import RipplingIntegration from "./RipplingIntegration";
import { invalidateCostCodeCache } from "./useCostCodeMap";
const API = "https://erp-xe9u.onrender.com/api";
// New child component — add at the top of the file:
function SafetyDocTypesManagement({ setDialog }) {
  const [docTypes, setDocTypes] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    durationMonths: 12,
    mandatory: false,
  });

  useEffect(() => {
    fetchDocTypes();
  }, []);

  const fetchDocTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/company-profile/safety-doc-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocTypes(res.data);
    } catch {}
  };

  const handleSave = async () => {
    if (!form.name || !form.durationMonths) return;
    try {
      const token = localStorage.getItem("token");
      if (editingId) {
        await axios.put(
          `${API}/company-profile/safety-doc-types/${editingId}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await axios.post(`${API}/company-profile/safety-doc-types`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setIsAdding(false);
      setEditingId(null);
      setForm({ name: "", durationMonths: 12, mandatory: false });
      fetchDocTypes();
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/company-profile/safety-doc-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchDocTypes();
    } catch {}
  };

  const handleToggleActive = async (dt) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/company-profile/safety-doc-types/${dt._id}`,
        { active: !dt.active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchDocTypes();
    } catch {}
  };

  const inputCls =
    "mt-1 block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
  const labelCls =
    "block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1";

  return (
    <div className="mt-8 border-t border-gray-700 pt-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-white">
            Safety Document Types
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Define required certifications and their validity periods
          </p>
        </div>
        <button
          onClick={() => {
            setIsAdding(true);
            setEditingId(null);
            setForm({ name: "", durationMonths: 12, mandatory: false });
          }}
          className="flex items-center gap-2 bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Type
        </button>
      </div>

      {/* Add / edit inline form */}
      {isAdding && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-4">
          <p className="text-xs font-semibold text-orange-400 uppercase tracking-widest mb-4">
            {editingId ? "Edit document type" : "New document type"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className={labelCls}>Document Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="e.g., First Aid Certificate"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Valid For (months)</label>
              <input
                type="number"
                min={1}
                value={form.durationMonths}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    durationMonths: parseInt(e.target.value) || 1,
                  }))
                }
                className={inputCls}
              />
            </div>
            <div className="flex flex-col justify-end">
              <label className="flex items-center justify-between bg-gray-700/40 border border-gray-700 rounded-lg px-4 py-3 cursor-pointer">
                <span className="text-sm text-gray-300">Mandatory</span>
                <button
                  type="button"
                  onClick={() =>
                    setForm((p) => ({ ...p, mandatory: !p.mandatory }))
                  }
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${form.mandatory ? "bg-orange-400" : "bg-gray-600"}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${form.mandatory ? "translate-x-5" : "translate-x-0"}`}
                  />
                </button>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => {
                setIsAdding(false);
                setEditingId(null);
              }}
              className="px-4 py-2 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm"
            >
              {editingId ? "Save Changes" : "Save Type"}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {docTypes.length > 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-5 py-3 font-semibold">Document</th>
                <th className="text-left px-4 py-3 font-semibold">Valid for</th>
                <th className="text-left px-4 py-3 font-semibold">Mandatory</th>
                <th className="text-left px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {docTypes.map((dt, i) => (
                <tr
                  key={dt._id}
                  className={`${i < docTypes.length - 1 ? "border-b border-gray-700/50" : ""} ${!dt.active ? "opacity-50" : ""}`}
                >
                  <td className="px-5 py-3.5 font-medium text-gray-100">
                    {dt.name}
                  </td>
                  <td className="px-4 py-3.5 text-gray-400">
                    {dt.durationMonths >= 12 && dt.durationMonths % 12 === 0
                      ? `${dt.durationMonths / 12} yr${dt.durationMonths / 12 !== 1 ? "s" : ""}`
                      : `${dt.durationMonths} mo`}
                  </td>
                  <td className="px-4 py-3.5">
                    {dt.mandatory ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-orange-400/10 text-orange-300 ring-1 ring-inset ring-orange-400/20">
                        Required
                      </span>
                    ) : (
                      <span className="text-gray-600 text-xs">Optional</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <button
                      onClick={() => handleToggleActive(dt)}
                      className={`relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none ${dt.active ? "bg-orange-400" : "bg-gray-600"}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${dt.active ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => {
                          setEditingId(dt._id);
                          setForm({
                            name: dt.name,
                            durationMonths: dt.durationMonths,
                            mandatory: dt.mandatory,
                          });
                          setIsAdding(true);
                        }}
                        className="text-xs text-orange-300 hover:text-orange-200 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(dt._id)}
                        className="text-xs text-gray-500 hover:text-red-400 font-medium transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-800 border border-dashed border-gray-700 rounded-xl px-6 py-10 text-center">
          <p className="text-sm text-gray-500">
            No safety document types defined yet.
          </p>
          <button
            onClick={() => setIsAdding(true)}
            className="mt-2 text-xs text-orange-400 hover:text-orange-300"
          >
            Add one now →
          </button>
        </div>
      )}
    </div>
  );
}

function CostCodesManagement({ setDialog }) {
  const [costCodes, setCostCodes] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ code: "", name: "" });
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchCostCodes();
  }, []);

  const fetchCostCodes = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/company-profile/cost-codes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCostCodes(res.data);
    } catch {}
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) return;
    try {
      const token = localStorage.getItem("token");
      if (editingId) {
        await axios.put(
          `${API}/company-profile/cost-codes/${editingId}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await axios.post(`${API}/company-profile/cost-codes`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setIsAdding(false);
      setEditingId(null);
      setForm({ code: "", name: "" });
      fetchCostCodes();
      invalidateCostCodeCache();
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/company-profile/cost-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCostCodes();
      invalidateCostCodeCache();
    } catch {}
  };

  const handleToggleActive = async (cc) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/company-profile/cost-codes/${cc._id}`,
        { active: !cc.active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchCostCodes();
      invalidateCostCodeCache();
    } catch {}
  };

  const inputCls =
    "mt-1 block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
  const labelCls =
    "block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1";

  const filtered = costCodes.filter(
    (c) =>
      c.code.toLowerCase().includes(search.toLowerCase()) ||
      c.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="mt-8 border-t border-gray-700 pt-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-white">Cost Codes</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Define CSI cost codes used across estimates and work orders
          </p>
        </div>
        <button
          onClick={() => {
            setIsAdding(true);
            setEditingId(null);
            setForm({ code: "", name: "" });
          }}
          className="flex items-center gap-2 bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Code
        </button>
      </div>

      {/* Inline add / edit form */}
      {isAdding && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-4">
          <p className="text-xs font-semibold text-orange-400 uppercase tracking-widest mb-4">
            {editingId ? "Edit cost code" : "New cost code"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Section Number</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) =>
                  setForm((p) => ({ ...p, code: e.target.value }))
                }
                placeholder="e.g. 09 29 00"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Section Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder="e.g. Gypsum Board"
                className={inputCls}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => {
                setIsAdding(false);
                setEditingId(null);
              }}
              className="px-4 py-2 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-lg transition-colors shadow-sm"
            >
              {editingId ? "Save Changes" : "Save Code"}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      {costCodes.length > 0 && (
        <div className="mb-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search codes or names…"
            className="w-full sm:w-72 rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors"
          />
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-5 py-3 font-semibold">Section #</th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((cc, i) => (
                <tr
                  key={cc._id}
                  className={`${i < filtered.length - 1 ? "border-b border-gray-700/50" : ""} ${!cc.active ? "opacity-50" : ""}`}
                >
                  <td className="px-5 py-3.5 font-mono text-xs text-orange-300 font-semibold tracking-wide">
                    {cc.code}
                  </td>
                  <td className="px-4 py-3.5 font-medium text-gray-100">
                    {cc.name}
                  </td>
                  <td className="px-4 py-3.5">
                    <button
                      onClick={() => handleToggleActive(cc)}
                      className={`relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none ${cc.active ? "bg-orange-400" : "bg-gray-600"}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${cc.active ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => {
                          setEditingId(cc._id);
                          setForm({ code: cc.code, name: cc.name });
                          setIsAdding(true);
                        }}
                        className="text-xs text-orange-300 hover:text-orange-200 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(cc._id)}
                        className="text-xs text-gray-500 hover:text-red-400 font-medium transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : costCodes.length === 0 ? (
        <div className="bg-gray-800 border border-dashed border-gray-700 rounded-xl px-6 py-10 text-center">
          <p className="text-sm text-gray-500">No cost codes defined yet.</p>
          <button
            onClick={() => setIsAdding(true)}
            className="mt-2 text-xs text-orange-400 hover:text-orange-300"
          >
            Add one now →
          </button>
        </div>
      ) : (
        <div className="bg-gray-800 border border-dashed border-gray-700 rounded-xl px-6 py-6 text-center">
          <p className="text-sm text-gray-500">No results for "{search}".</p>
        </div>
      )}
    </div>
  );
}

export default function CompanyProfileManagement() {
  const [isAddingProfile, setIsAddingProfile] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingRates, setIsEditingRates] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [unions, setUnions] = useState([]);
  const [quickBooksTaxCodes, setQuickBooksTaxCodes] = useState([]);
  const [quickBooksItems, setQuickBooksItems] = useState([]);
  const [isQuickBooksConnected, setIsQuickBooksConnected] = useState(false);
  const [taxRateData, setTaxRateData] = useState({
    taxRate: "0",
    taxCodeId: "",
    defaultItemId: "",
  });
  const [profileData, setProfileData] = useState({
    companyName: "",
    address: { street: "", city: "", state: "", zip: "", country: "" },
    active: true,
    sharePointSite: "",
    defaultTax: { taxCodeId: null, taxRate: 0, defaultItemId: null },
    wsib: 0,
    employerNumber: "",
    plugins: { quickbooks: { lastSynced: null } },
  });
  const [logoFile, setLogoFile] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const [
        companyProfileResponse,
        quickBooksStatusResponse,
        taxRateResponse,
        unionsResponse,
      ] = await Promise.all([
        axios
          .get(`${API}/company-profile`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch((err) =>
            err.response?.status === 404 ? { data: null } : Promise.reject(err),
          ),
        axios.get(`${API}/company-profile/quickbooks-status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios
          .get(`${API}/company-profile/tax-rate`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch((err) =>
            err.response?.status === 404
              ? { data: { taxRate: "0", taxCodeId: "", defaultItemId: "" } }
              : Promise.reject(err),
          ),
        axios
          .get(`${API}/company-profile/unions`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: [] })),
      ]);

      setCompanyProfile(companyProfileResponse.data);
      if (companyProfileResponse.data) {
        setProfileData({
          companyName: companyProfileResponse.data.companyName || "",
          address: companyProfileResponse.data.address || {
            street: "",
            city: "",
            state: "",
            zip: "",
            country: "",
          },
          active: companyProfileResponse.data.active ?? true,
          sharePointSite: companyProfileResponse.data.sharePointSite || "",
          defaultTax: companyProfileResponse.data.defaultTax || {
            taxCodeId: null,
            taxRate: 0,
            defaultItemId: null,
          },
          wsib: companyProfileResponse.data.wsib || 0,
          employerNumber: companyProfileResponse.data.employerNumber || "",
          plugins: companyProfileResponse.data.plugins || {
            quickbooks: { lastSynced: null },
          },
        });
      }

      setIsQuickBooksConnected(
        quickBooksStatusResponse.data.isQuickBooksConnected || false,
      );
      setTaxRateData({
        taxRate: taxRateResponse.data.taxRate || "0",
        taxCodeId: taxRateResponse.data.taxCodeId || "",
        defaultItemId: taxRateResponse.data.defaultItemId || "",
      });
      setUnions(Array.isArray(unionsResponse.data) ? unionsResponse.data : []);

      if (quickBooksStatusResponse.data.isQuickBooksConnected) {
        const [taxCodesResponse, itemsResponse] = await Promise.all([
          axios.get(`${API}/quickbooks/tax-codes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/quickbooks/items`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setQuickBooksTaxCodes(taxCodesResponse.data);
        setQuickBooksItems(itemsResponse.data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.status === 404
            ? "Company profile not found."
            : `Failed to load company profile: ${err.message}`,
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setProfileData((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value },
      }));
    } else if (name === "defaultTax.taxCodeId") {
      const selected = quickBooksTaxCodes.find((t) => t.id === value);
      setProfileData((prev) => ({
        ...prev,
        defaultTax: {
          ...prev.defaultTax,
          taxCodeId: value,
          taxRate: selected?.taxRates[0]?.rate || 0,
        },
      }));
    } else if (name === "defaultTax.taxRate") {
      setProfileData((prev) => ({
        ...prev,
        defaultTax: { ...prev.defaultTax, taxRate: parseFloat(value) || 0 },
      }));
    } else if (name === "defaultTax.defaultItemId") {
      setProfileData((prev) => ({
        ...prev,
        defaultTax: { ...prev.defaultTax, defaultItemId: value },
      }));
    } else if (name === "wsib") {
      setProfileData((prev) => ({ ...prev, wsib: parseFloat(value) || 0 }));
    } else {
      setProfileData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddOrUpdateProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("companyName", profileData.companyName);
      formData.append("address[street]", profileData.address.street);
      formData.append("address[city]", profileData.address.city);
      formData.append("address[state]", profileData.address.state);
      formData.append("address[zip]", profileData.address.zip);
      formData.append("address[country]", profileData.address.country);
      formData.append("active", profileData.active);
      formData.append("sharePointSite", profileData.sharePointSite);
      formData.append(
        "defaultTax[taxCodeId]",
        profileData.defaultTax.taxCodeId || "",
      );
      formData.append(
        "defaultTax[taxRate]",
        profileData.defaultTax.taxRate || 0,
      );
      formData.append(
        "defaultTax[defaultItemId]",
        profileData.defaultTax.defaultItemId || "",
      );
      formData.append("wsib", profileData.wsib || 0);
      formData.append("employerNumber", profileData.employerNumber || "");
      if (logoFile) formData.append("logo", logoFile);

      const method = companyProfile && isEditingProfile ? "put" : "post";
      const response = await axios[method](`${API}/company-profile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (isQuickBooksConnected && profileData.defaultTax.taxCodeId) {
        await axios.post(
          `${API}/quickbooks/set-default-tax`,
          { taxCodeId: profileData.defaultTax.taxCodeId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      setCompanyProfile(response.data);
      setIsAddingProfile(false);
      setIsEditingProfile(false);
      setLogoFile(null);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: `Company profile ${isEditingProfile ? "updated" : "added"} successfully!`,
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    } catch (err) {
      console.error("Error saving company profile:", err);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to save company profile. Please try again.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
      });
    }
  };

  const handleEditClick = () => {
    if (companyProfile) {
      setProfileData({
        companyName: companyProfile.companyName || "",
        address: companyProfile.address || {
          street: "",
          city: "",
          state: "",
          zip: "",
          country: "",
        },
        active: companyProfile.active ?? true,
        sharePointSite: companyProfile.sharePointSite || "",
        defaultTax: {
          taxCodeId: taxRateData.taxCodeId || null,
          taxRate: parseFloat(taxRateData.taxRate) || 0,
          defaultItemId: taxRateData.defaultItemId || null,
        },
        wsib: companyProfile.wsib || 0,
        employerNumber: companyProfile.employerNumber || "",
        plugins: companyProfile.plugins || { quickbooks: { lastSynced: null } },
      });
      setIsEditingProfile(true);
    }
  };

  const handleCloseModal = () => {
    setIsAddingProfile(false);
    setIsEditingProfile(false);
    setLogoFile(null);
  };

  const handleCloseRates = () => setIsEditingRates(false);

  const inputClass =
    "mt-1 block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
  const labelClass =
    "block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1";

  const InfoRow = ({ label, value }) => (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
        {label}
      </p>
      <p className="text-sm text-gray-200">
        {value || <span className="text-gray-600">—</span>}
      </p>
    </div>
  );

  const SectionHeader = ({ title }) => (
    <div className="col-span-2 flex items-center gap-3 pt-2">
      <span className="text-xs font-semibold text-orange-400 uppercase tracking-widest">
        {title}
      </span>
      <div className="flex-1 h-px bg-gray-700" />
    </div>
  );

  // isEditingProfile is true while the modal is open — passed to LaborRatesManagement
  // so the edit table is visible alongside the modal (below it on the page).
  const isInEditMode = isAddingProfile || isEditingProfile;

  return (
    <div className="mt-10 border-t border-gray-700 pt-8">
      <AlertDialog
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="OK"
        cancelText="Close"
        open={dialog.open}
        onClose={() => setDialog((p) => ({ ...p, open: false }))}
        onConfirm={dialog.onConfirm}
      />

      {/* Section header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-white">
            Company Profile
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Company details, tax settings, and integrations
          </p>
        </div>
        {!companyProfile ? (
          <button
            onClick={() => setIsAddingProfile(true)}
            className="flex items-center gap-2 bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Profile
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditingRates(true)}
              className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium px-4 py-2 rounded-lg transition-colors border border-gray-600"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Edit Rates
            </button>
            <button
              onClick={handleEditClick}
              className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium px-4 py-2 rounded-lg transition-colors border border-gray-600"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit Profile
            </button>
          </div>
        )}
      </div>

      {/* Profile card */}
      {companyProfile ? (
        <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-lg">
          {/* Logo + name banner */}
          <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-700">
            {companyProfile.logo ? (
              <img
                src={companyProfile.logo}
                alt="Company Logo"
                className="w-14 h-14 rounded-lg object-contain bg-gray-700 border border-gray-600 p-1"
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-gray-700 border border-gray-600 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-base font-semibold text-white">
                {companyProfile.companyName}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {[
                  companyProfile.address?.city,
                  companyProfile.address?.state,
                  companyProfile.address?.country,
                ]
                  .filter(Boolean)
                  .join(", ") || "No address set"}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className={`w-1.5 h-1.5 rounded-full ${companyProfile.active ? "bg-green-400" : "bg-red-400"}`}
              />
              <span
                className={`text-xs font-medium ${companyProfile.active ? "text-green-400" : "text-red-400"}`}
              >
                {companyProfile.active ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 px-6 py-5 border-b border-gray-700">
            <InfoRow label="Street" value={companyProfile.address?.street} />
            <InfoRow label="City" value={companyProfile.address?.city} />
            <InfoRow
              label="State / Province"
              value={companyProfile.address?.state}
            />
            <InfoRow label="Zip / Postal" value={companyProfile.address?.zip} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 px-6 py-5 border-b border-gray-700">
            <InfoRow
              label="WSIB Rate"
              value={companyProfile.wsib ? `${companyProfile.wsib}%` : null}
            />
            <InfoRow
              label="Default Tax"
              value={
                isQuickBooksConnected
                  ? quickBooksTaxCodes.find(
                      (t) => t.id === taxRateData.taxCodeId,
                    )?.name
                  : taxRateData.taxRate
                    ? `${taxRateData.taxRate}%`
                    : null
              }
            />
            <InfoRow
              label="Default QB Item"
              value={
                isQuickBooksConnected
                  ? quickBooksItems.find(
                      (i) => i.id === taxRateData.defaultItemId,
                    )?.name
                  : null
              }
            />
            <InfoRow label="SharePoint" value={companyProfile.sharePointSite} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 px-6 py-5">
            <InfoRow label="Total Users" value={companyProfile.numberOfUsers} />
            <InfoRow label="Foremen" value={companyProfile.numberOfForemen} />
            <InfoRow
              label="Union Employer #"
              value={companyProfile.employerNumber}
            />
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                QuickBooks
              </p>
              <div className="flex items-center gap-1.5">
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${isQuickBooksConnected ? "bg-green-400" : "bg-gray-600"}`}
                />
                <span
                  className={`text-sm ${isQuickBooksConnected ? "text-green-400" : "text-gray-500"}`}
                >
                  {isQuickBooksConnected ? "Connected" : "Not connected"}
                </span>
              </div>
              {isQuickBooksConnected &&
                companyProfile.plugins?.quickbooks?.lastSynced && (
                  <p className="text-xs text-gray-600 mt-0.5">
                    Synced{" "}
                    {new Date(
                      companyProfile.plugins.quickbooks.lastSynced,
                    ).toLocaleDateString()}
                  </p>
                )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 border border-dashed border-gray-700 rounded-xl px-6 py-12 text-center">
          <svg
            className="w-10 h-10 text-gray-600 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p className="text-sm text-gray-500">
            No company profile set up yet.
          </p>
          <button
            onClick={() => setIsAddingProfile(true)}
            className="mt-3 text-xs text-orange-400 hover:text-orange-300 transition-colors"
          >
            Create one now →
          </button>
        </div>
      )}

      {/* ── Labor Rates — read-only by default, edit mode when Edit Rates is clicked ── */}
      <LaborRatesManagement
        setDialog={setDialog}
        isEditing={isEditingRates}
        onClose={handleCloseRates}
        unions={unions}
      />

      <SafetyDocTypesManagement setDialog={setDialog} />
      <CostCodesManagement setDialog={setDialog} />
      <SubAgreementTemplatesManagement setDialog={setDialog} />
      {/* ── Add / Edit Modal — Headless UI v2 compatible ── */}
      <Transition show={isInEditMode}>
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50 p-4
                     transition duration-200 data-[closed]:opacity-0"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div
            className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl border border-gray-700
                          flex flex-col max-h-[90vh]
                          transition duration-200 data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
              <div>
                <h3 className="text-base font-semibold text-white">
                  {isEditingProfile
                    ? "Edit Company Profile"
                    : "Create Company Profile"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isEditingProfile
                    ? "Update your company details"
                    : "Set up your company information"}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-300 p-1 rounded-md hover:bg-gray-700 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 px-6 py-5">
              <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                <SectionHeader title="Company" />
                <div className="col-span-2">
                  <label className={labelClass}>Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={profileData.companyName}
                    onChange={handleProfileChange}
                    placeholder="Acme Construction Inc."
                    className={inputClass}
                  />
                </div>

                <SectionHeader title="Address" />
                <div className="col-span-2">
                  <label className={labelClass}>Street Address</label>
                  <input
                    type="text"
                    name="address.street"
                    value={profileData.address.street}
                    onChange={handleProfileChange}
                    placeholder="123 Main St"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    name="address.city"
                    value={profileData.address.city}
                    onChange={handleProfileChange}
                    placeholder="Toronto"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>State / Province</label>
                  <input
                    type="text"
                    name="address.state"
                    value={profileData.address.state}
                    onChange={handleProfileChange}
                    placeholder="ON"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Zip / Postal Code</label>
                  <input
                    type="text"
                    name="address.zip"
                    value={profileData.address.zip}
                    onChange={handleProfileChange}
                    placeholder="M5V 2T6"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Country</label>
                  <input
                    type="text"
                    name="address.country"
                    value={profileData.address.country}
                    onChange={handleProfileChange}
                    placeholder="Canada"
                    className={inputClass}
                  />
                </div>

                <SectionHeader title="Settings" />
                <div>
                  <label className={labelClass}>WSIB Rate (%)</label>
                  <input
                    type="number"
                    name="wsib"
                    value={profileData.wsib}
                    onChange={handleProfileChange}
                    step="0.01"
                    min="0"
                    max="100"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>SharePoint Site</label>
                  <input
                    type="text"
                    name="sharePointSite"
                    value={profileData.sharePointSite}
                    onChange={handleProfileChange}
                    placeholder="/sites/YourSiteName"
                    className={inputClass}
                  />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Union Employer Number</label>
                  <input
                    type="text"
                    name="employerNumber"
                    value={profileData.employerNumber}
                    onChange={handleProfileChange}
                    placeholder="e.g. 1701352"
                    maxLength={30}
                    className={inputClass}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Printed on the union dues remittance (DALI) exports.
                  </p>
                </div>

                <SectionHeader title="Tax" />
                {isQuickBooksConnected ? (
                  <>
                    <div>
                      <label className={labelClass}>QuickBooks Tax Code</label>
                      <select
                        name="defaultTax.taxCodeId"
                        value={profileData.defaultTax.taxCodeId || ""}
                        onChange={handleProfileChange}
                        className={inputClass}
                      >
                        <option value="">Select tax code</option>
                        {quickBooksTaxCodes.map((tax) => (
                          <option key={tax.id} value={tax.id}>
                            {`${tax.name} (${tax.taxRates[0]?.rate || 0}%)`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>
                        QuickBooks Default Item
                      </label>
                      <select
                        name="defaultTax.defaultItemId"
                        value={profileData.defaultTax.defaultItemId || ""}
                        onChange={handleProfileChange}
                        className={inputClass}
                      >
                        <option value="">Select item</option>
                        {quickBooksItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className={labelClass}>Default Tax Rate (%)</label>
                    <input
                      type="number"
                      name="defaultTax.taxRate"
                      value={profileData.defaultTax.taxRate}
                      onChange={handleProfileChange}
                      step="0.01"
                      min="0"
                      max="100"
                      className={inputClass}
                    />
                  </div>
                )}

                <SectionHeader title="Branding" />
                <div className="col-span-2">
                  <label className={labelClass}>Company Logo</label>
                  <label
                    className="mt-1 flex items-center gap-3 px-4 py-3 bg-gray-700 border border-gray-600
                                    border-dashed rounded-md cursor-pointer hover:border-orange-400
                                    hover:bg-gray-700/80 transition-colors"
                  >
                    <svg
                      className="w-5 h-5 text-gray-500 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm text-gray-400">
                      {logoFile
                        ? logoFile.name
                        : "Click to upload logo (JPG, PNG)"}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={(e) => setLogoFile(e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                </div>

                <div className="col-span-2 bg-gray-700/40 rounded-lg p-4 border border-gray-700">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm text-gray-300">
                      Company Active
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setProfileData((p) => ({ ...p, active: !p.active }))
                      }
                      className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none
                                  ${profileData.active ? "bg-orange-400" : "bg-gray-600"}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow
                                        transition-transform duration-200
                                        ${profileData.active ? "translate-x-5" : "translate-x-0"}`}
                      />
                    </button>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600
                           rounded-md transition-colors border border-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddOrUpdateProfile}
                className="px-5 py-2 text-sm font-semibold text-white bg-orange-400 hover:bg-orange-500
                           rounded-md transition-colors shadow-sm"
              >
                {isEditingProfile ? "Save Changes" : "Create Profile"}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}
